"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LinkFieldLogic = void 0;
const difference_1 = __importDefault(require("lodash-es/difference"));
const clipboard_1 = require("../../clipboard/clipboard");
const types_1 = require("@smartsuite/types");
const field_logic_1 = require("../../field-type-registry/field-logic");
const comparisons_1 = require("../../comparisons/comparisons");
const fields_1 = require("../../utils/fields");
const link_1 = require("../../utils/link");
const google_sheet_1 = require("../../utils/google-sheet");
const migration_1 = require("../../utils/migration");
const text_1 = require("../../utils/text");
const intl_1 = require("../../locale/intl");
const link_fieldtype_1 = require("../../safe-field-value/text-fields/link-field/link.fieldtype");
const texts_1 = require("../../texts");
const filter_1 = require("../../utils/filter");
const validation_1 = require("../../validation/validation");
const bulk_update_1 = require("../../utils/bulk-update");
const linkifyItInstance = (0, link_1.linkify)();
class LinkFieldLogic extends field_logic_1.BaseFieldTypeLogic {
    constructor() {
        super(...arguments);
        this.name = "linkfield";
        this.label = (0, intl_1.$t)(texts_1.linkFieldText.link);
        this.icon = "link";
        this.iconColor = "#FF9210";
        this.placeholder = (0, intl_1.$t)(texts_1.linkFieldText.enter_a_website_address);
        this.searchSynonyms = ["url", "www"];
        // @ts-expect-error: Need to resolve
        this.comparisons = {
            is: comparisons_1.isLink,
            is_not: comparisons_1.isNotLink,
            is_empty: comparisons_1.isEmptyList,
            is_not_empty: comparisons_1.isNotEmptyList,
            contains: comparisons_1.containsList,
            not_contains: comparisons_1.notContainsList,
        };
        this.fieldValueGetter = new link_fieldtype_1.LinkFieldValueGetter();
        this.clipboardPasteRules = {
            linkfield: clipboard_1.clipboardLinkSelf,
            linkedrecordfield: clipboard_1.clipboardReferenceFieldToLink,
            textfield: clipboard_1.clipboardTextToLink,
            recordtitlefield: clipboard_1.clipboardTextToLink,
            textareafield: clipboard_1.clipboardTextToLink,
            richtextareafield: clipboard_1.clipboardTextToLink,
            socialnetworkfield: clipboard_1.clipboardTextToLink,
            tagsfield: clipboard_1.clipboardTextToLink,
            unknownfield: clipboard_1.clipboardTextToLink,
        };
    }
    get validators() {
        return { requiredList: validation_1.requiredList, linkField: validation_1.linkField };
    }
    getInlineEditRules(field) {
        const isSingle = field.params.entries_allowed === "single";
        const baseInlineEditRules = super.getInlineEditRules(field);
        const isFaviconDisplayFormat = field.params.display_format === "favicon_only";
        const isFaviconMultiple = isFaviconDisplayFormat && !isSingle;
        const switchToInputControl = isSingle || isFaviconDisplayFormat
            ? "OnSelectedCellClick"
            : "OnCellSelect";
        const cellPresenceEventMode = isSingle || isFaviconDisplayFormat ? "OnEdit" : "OnModalOpen";
        const multipleModeSaveKeys = isFaviconDisplayFormat ? [] : types_1.navigationKeys;
        return {
            ...baseInlineEditRules,
            closeWhenClickOutside: isSingle,
            deleteValueOnFirstKeypress: isSingle,
            switchToInputControl,
            editorWidth: "100%",
            enableCopyEventInEditMode: !isSingle && !isFaviconDisplayFormat,
            enablePasteEventInEditMode: !isSingle && !isFaviconDisplayFormat,
            saveKeys: isSingle ? baseInlineEditRules.saveKeys : multipleModeSaveKeys,
            inputControlEditMode: "OnShow",
            cellPresenceEventMode,
            rollbackOnPressEscapeKey: isSingle || isFaviconDisplayFormat,
            editOnStartTyping: !isFaviconMultiple,
            needUseDefaultValue: (value, isDirty) => {
                const isNeedUseDefaultValue = (0, fields_1.needUseDefaultFieldValue)(field, value);
                if (isSingle) {
                    return isNeedUseDefaultValue;
                }
                return isDirty && isNeedUseDefaultValue;
            },
            spaceKeyOpensRecordModal: (isEditMode) => !isEditMode || !isSingle,
            canDeleteValue: false,
        };
    }
    processFilterValue(filterValue, comparison) {
        /**
         * We allow specifying multiple filter values separated by ;,' and space.
         * They are applied as OR.
         */
        const filterComparisons = ["is", "is_not", "contains", "not_contains"];
        if (filterComparisons.includes(comparison)) {
            return (0, text_1.processMultipleFilterValue)(filterValue);
        }
        return filterValue;
    }
    filterToRecordValue(filterValue, comparison, field) {
        if (comparison !== "is" || !filterValue) {
            throw new types_1.FilterToRecordValueConversionError(field, filterValue, comparison);
        }
        const value = (0, text_1.processMultipleFilterValue)(filterValue);
        if (value.some((item) => !linkifyItInstance.test(item))) {
            throw new types_1.FilterToRecordValueConversionError(field, filterValue, comparison);
        }
        return value;
    }
    getEmptyValue() {
        return [];
    }
    isValueEmpty(fieldValue) {
        return !(fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.length);
    }
    getInitialFieldValue(startValue) {
        return startValue ? [startValue] : this.getEmptyValue();
    }
    getGroupValues(fieldValue) {
        if (!Array.isArray(fieldValue) || !fieldValue.length) {
            return [[]];
        }
        return fieldValue
            .filter(filter_1.filterUniqueValues)
            .map((valueItem) => [valueItem]);
    }
    valueToString(fieldValue, _field) {
        var _a;
        return (_a = fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.join(", ")) !== null && _a !== void 0 ? _a : "";
    }
    sortKey(fieldValue, field, _userPreferences, _record) {
        return this.valueToString(fieldValue, field).toLowerCase();
    }
    groupBySortKey(value) {
        return this.groupByKey(value);
    }
    groupByKey(fieldValue) {
        return fieldValue
            .map((valueItem) => { var _a; return (_a = linkifyItInstance.match(valueItem)) === null || _a === void 0 ? void 0 : _a[0].url; })
            .join(", ")
            .toLowerCase();
    }
    prepareValueForClipboard(fieldValue, _field) {
        return { text: fieldValue.join(" ; "), rawValue: fieldValue };
    }
    formatActivityRecordChangeValues(previousValue, currentValue) {
        if (this.isValueEmpty(previousValue) || this.isValueEmpty(currentValue)) {
            return { previousValue, currentValue };
        }
        const removedValues = (0, difference_1.default)(previousValue, currentValue);
        const addedValues = (0, difference_1.default)(currentValue, previousValue);
        return { previousValue: removedValues, currentValue: addedValues };
    }
    replaceValue(field, record, sourceValue = [], targetValue = [], _userPreferences) {
        return (0, fields_1.replaceValueInList)(field, record, sourceValue, targetValue);
    }
    getLinkedRecordFieldSmartWidth(_field) {
        return {
            defaultWidht: 170,
            minWidth: 90,
        };
    }
    valueToGoogleSheet(fieldValue, field) {
        if (this.isValueEmpty(fieldValue)) {
            return (0, google_sheet_1.makeRowDataValues)("", "stringValue");
        }
        const value = fieldValue.join("; ");
        return (0, google_sheet_1.makeRowDataValues)(value, "stringValue");
    }
    migrateField(fieldTo, fieldFrom) {
        var _a;
        const migratedField = super.migrateField(fieldTo, fieldFrom);
        return (0, migration_1.migrateDisplayFormat)(migratedField, types_1.linkDisplayFormats, (_a = fieldTo.params.display_format) !== null && _a !== void 0 ? _a : "");
    }
    getContentSize(fieldValue, field, userPreferences, record, font) {
        const { display_format } = field.params;
        const onlyIcon = display_format === "favicon_only";
        if (onlyIcon) {
            const favicon = 29;
            return favicon * fieldValue.length;
        }
        const hasIcon = display_format === "link_with_favicon";
        const faviconInText = 22;
        const favicons = hasIcon ? faviconInText * fieldValue.length : 0;
        const size = super.getContentSize(fieldValue, field, userPreferences, record, font);
        return size + favicons;
    }
    /**
     * Applies a bulk update to a field value.
     */
    applyBulkUpdate(field, value, update) {
        if (field.params.entries_allowed === "single") {
            return super.applyBulkUpdate(field, value, update);
        }
        return (0, bulk_update_1.applyBulkUpdateToPrimitiveArray)(value, update);
    }
}
exports.LinkFieldLogic = LinkFieldLogic;
